import { memo, useCallback, useMemo } from 'react'
import { useNavigate, useLocation, createSearchParams } from 'react-router-dom'

import { ROUTES, usePackageInfo } from '@/utils'
import useUpdatePackageInfo from '@/utils/hooks/useUpdatePackageInfo'
import { Box } from '@/components/Layout'
import { Text } from '@/components/Data'
import { SummaryCardActions, useToast } from '@/components'
import { useTranslations } from '@/core/translation'
import { useRemoveAddonsMutation } from '@/redux/package'

import { summarySidebarStyles as styles } from '../SummarySidebar/SummarySidebar.style'

const AddOnSummary = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { currentPackage, isSingleProduct } = usePackageInfo()
  const t = useTranslations('addOn')
  const tGeneral = useTranslations('general')
  const toast = useToast()
  const { refetchPackageInfo } = useUpdatePackageInfo()
  const [removeAddons] = useRemoveAddonsMutation()

  const addons = useMemo(
    () => currentPackage?.cartInfo?.addons || [],
    [currentPackage?.cartInfo?.addons],
  )

  const addonsItem = useMemo(
    () =>
      addons.map((addon) => ({
        id: addon.details.id,
        title: addon.details.name,
        descriptionFull: addon.details.descriptionFull,
        selectedQuantity: addon.quantity,
      })),
    [addons],
  )

  const handleClick = useCallback(() => {
    navigate({
      pathname: ROUTES.PACKAGE.ADD_ONS,
      search: createSearchParams({
        sessionId: currentPackage?.sessionId ?? '',
        modify: 'true',
        redirectUrl: location.pathname,
      }).toString(),
    })
  }, [currentPackage?.sessionId, navigate, location.pathname])

  const onAddonRemoveClick = useCallback(
    async (addonId: string) => {
      try {
        await removeAddons({
          sessionId: currentPackage?.sessionId ?? '',
          addonId,
        }).unwrap()
        refetchPackageInfo(currentPackage?.sessionId ?? '')
      } catch {
        toast(tGeneral('apiError'))
      }
    },
    [currentPackage?.sessionId, refetchPackageInfo, removeAddons, tGeneral, toast],
  )

  if (addonsItem.length === 0) return

  return (
    <>
      {addonsItem.map((addon) => (
        <Box sx={styles.addOnCard} key={addon.id}>
          <Box>
            <Text sx={styles.addOnTitle}>{addon.title}</Text>
            <Text
              sx={styles.addOnDescriptionFull}
              dangerouslySetInnerHTML={{ __html: addon.descriptionFull || '' }}
            />
            <Box>
              <Text sx={styles.addonsQuantityText} component={'span'}>
                {t('quantity')}:
              </Text>
              <Text sx={styles.addonsQuantity} component={'span'}>
                {addon.selectedQuantity}
              </Text>
            </Box>
          </Box>
          <SummaryCardActions
            onEditClick={handleClick}
            onRemoveClick={() => onAddonRemoveClick(addon.id)}
            isRemoveActionDisabled={isSingleProduct}
          />
        </Box>
      ))}
    </>
  )
}

export default memo(AddOnSummary)
